import React, { useMemo, useState } from "react";
import {
    Button, Modal, Input, Tree, Skeleton, Tooltip, Progress
} from "antd";
import {
    CloseOutlined, FolderOpenFilled, FolderFilled,
    WarningOutlined
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import useSearchInTree from "../../hooks/Tree/useSearchInTree";
import useCategoryAnalysisTree from "../../hooks/Tree/useCategoryAnalysisTree";
import useCountSegmentInTree from "../../hooks/Tree/useCountSegmentInTree";
import { setAllCategoryAnalysisTreeData, setCheckedBehavioralSegments, setSelectedGeneralMarketCategoryIsLoading, updateDeepDiveGroupData, updateSelectedGeneralMarketCategory } from "../../store/components/Reports.slice";
import { setReportBuilderMode } from "../../store/components/ReportBuilder.slice";
import { loadMarketCategoryReports } from "../../utils/report/ReportsHelper";
import ReportsServices from "../../services/Reports.services";
import { getModuleCode, getSelectedAnalysis } from "../../utils/oneintelligence/OneIntelligenceHelper";
import { folderStyle, formatNumber, getName } from "../../utils/MainHelper";
import RenderComponent from "../General/RenderComponent";
import TrackFeatureServices from "../../services/TrackFeature.services";
import { setBreadItems } from "../../store/components/TreeMapReport.slice";
import { getAnalysisName } from "../../utils/analysis/AnalysisHelper";
import TreeProgress from "../General/TreeProgress";
import useTreeShowLowActivity from "../General/useTreeShowLowActivity";

function AddBehavioralSegmentModal({
    visible,
    onClose = () => { },
    data
}) {
    const dispatch = useDispatch();

    const [expandedKeys, setexpandedKeys] = useState([]);
    const [searchedText, setsearchedText] = useState("");
    const { marketCategoryTypeToAdd } = useSelector((state) => state.reports);
    const { currentSelectedMode } = useSelector((state) => state.reportBuilder);

    const analysisObj = getSelectedAnalysis(currentSelectedMode)
    const {
        isLoading,
        treeData,
        percentage,
        checkedKeys,
        setcheckedKeys
    } = useCategoryAnalysisTree(visible, analysisObj);

    const { searchedData, loadingSearch } = useSearchInTree(searchedText, treeData);
    const { showLowActivity, lowActComponent } = useTreeShowLowActivity(analysisObj)

    const toDisplayData = useMemo(() => {
        const display = searchedText ? searchedData : treeData;

        if (!(analysisObj?.code || analysisObj?.key)?.includes("mokb")) return display
        if (showLowActivity) return display;
        return display.reduce((acc, d) => {
            const children = (d?.children || []).filter(
                (child) => !child.lowActivity
            );
            if (children.length) acc.push(d);
            return acc;
        }, []);
    }, [searchedText, analysisObj, searchedData, treeData, showLowActivity]);

    const moduleCode = getModuleCode();

    const segmentCount = useCountSegmentInTree(toDisplayData);

    const onExpand = (selected, info) => {
        setexpandedKeys(selected);
    };

    const onCheck = (checked, info) => {
        setcheckedKeys(info.checkedNodes);
    };

    const onOK = (addToTable) => {
        const allCategoriesCount = checkedKeys.reduce((count, item) => {
            if (!item.parentKey) count += 1
            return count
        }, 0)
        if (segmentCount === allCategoriesCount) {
            dispatch(setAllCategoryAnalysisTreeData(toDisplayData));
        } else dispatch(setAllCategoryAnalysisTreeData([]));
        dispatch(setCheckedBehavioralSegments(checkedKeys));

        if (marketCategoryTypeToAdd === "new") {
            dispatch(setBreadItems({ mode: "back", idx: 0 }));
            dispatch(setReportBuilderMode(analysisObj.key));
        } else if (addToTable !== true) {
            dispatch(setBreadItems({ mode: "back", idx: 0 }));
            loadMarketCategoryReports(false, undefined, undefined, { featureKey: "advanceAddToTableCategorysis" });
        } else if (data) {
            const { commonParams } = TrackFeatureServices.getMonitor({ featureKey: "advanceRunNewCategoryAnalysis" })
            ReportsServices.addBehaviouralSegmentItems(
                data,
                (groupData) => {
                    if (marketCategoryTypeToAdd === "generalTile") {
                        dispatch(setSelectedGeneralMarketCategoryIsLoading(true));
                    } else {
                        dispatch(updateDeepDiveGroupData({ groupData }));
                    }
                },
                (groupData, childList) => {
                    if (marketCategoryTypeToAdd === "generalTile") {
                        dispatch(updateSelectedGeneralMarketCategory({
                            groupData: {
                                ...groupData,
                                loadingSelectedTile: false
                            },
                            children: childList,
                            mergechildren: true
                        }));
                        dispatch(setSelectedGeneralMarketCategoryIsLoading(false));
                    } else {
                        dispatch(updateDeepDiveGroupData({ groupData, children: childList, mergechildren: true }));
                    }
                },
                commonParams);
        }
        onClose(false);
    };
    const checkIfHasParent = (() => checkedKeys.find((item) => item.children || item.tempChildren))();


    const emptyComponent = () => {
        if (loadingSearch || isLoading) return "";
        let tempText = "";
        if (searchedText && !searchedData.length) {
            tempText = "No result found.";
        } else if (!toDisplayData.length) {
            tempText = "No Data.";
        }
        if (!tempText) return "";
        return (
            <RenderComponent
                emptyProps={{
                    style: {
                        width: "calc(100% - 6mm)",
                        height: "95%"
                    },
                    description: tempText,
                    className: "d-flex-col align-items-center justify-content-center"
                }}
            />
        );
    };


    const getHeaderText = () => {
        if (data) return "Add Behavioral Segment";
        // let text = (analysisObj?.title || "Market Category");
        let text = getAnalysisName(analysisObj, moduleCode);
        if (analysisObj?.dontAddAnalysisText) return text;
        return text.includes("Analysis") ? text : text + " Analysis"
    }

    const searchedList = searchedText ? [searchedText] : []
    return (
        <Modal
            className="report--addbehaviouralsegment"
            style={{ width: "100%", height: "100%" }}
            open={visible}
            maskClosable={false}
            closable={false}
            title={(
                <div className="d-flex align-items-center" style={{ width: "100%" }}>
                    <div className="flex-1 ellipsis-text">
                        {getHeaderText()}
                    </div>
                    <Button
                        style={{ padding: 0, marginLeft: "5mm" }}
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() => {
                            onClose(false);
                        }}
                    />
                </div>
            )}
            footer={(
                <div className="d-flex justify-content-between">
                    <Button onClick={() => { onClose(false); }}>
                        Cancel
                    </Button>
                    <div className="d-flex">
                        {data
                            ? (
                                <>
                                    {marketCategoryTypeToAdd !== "generalTile" && (
                                        <Button
                                            disabled={(!checkIfHasParent || !checkedKeys.length)}
                                            onClick={onOK}
                                        >
                                            Set as new Category
                                        </Button>
                                    )}
                                    <Button type="primary" disabled={!checkedKeys.length} onClick={onOK.bind(null, true)}>Add to Table</Button>
                                </>
                            )
                            : <Button type="primary" disabled={!checkedKeys.length} onClick={onOK}>Load Reports</Button>}
                    </div>
                </div>
            )}
        >
            <div className="d-flex-col gap-1 report--addbehaviouralsegment--content">
                <div
                    className="d-flex align-items-center fs-14 fw-400 c-bg-400"
                    style={{
                        height: "5mm",
                        overflow: "hidden"
                    }}
                >
                    <span className="flex-1 ellipsis-text">
                        Add more or remove reports for this category.
                    </span>
                    <Button
                        style={{ padding: "1mm" }}
                        type="link"
                        onClick={() => {
                            const listTochecked = [];
                            const checkAllRecursive = (items) => {
                                items.forEach((child) => {
                                    listTochecked.push(child);
                                    if (child.children && child.children.length) {
                                        checkAllRecursive(child.children);
                                    }
                                });
                            };
                            checkAllRecursive(toDisplayData);
                            setcheckedKeys(listTochecked);
                        }}
                    >
                        Include all
                    </Button>
                    <span>|</span>
                    <Button
                        style={{ padding: "1mm" }}
                        type="link"
                        disabled={!checkedKeys.length}
                        onClick={() => {
                            setcheckedKeys([]);
                        }}
                    >
                        Exclude all
                    </Button>
                </div>
                <Input.Search
                    placeholder="Search"
                    style={{ width: "100%" }}
                    value={searchedText}
                    onChange={(e) => {
                        setsearchedText(e.target.value);
                    }}
                    onSearch={(e) => {
                        setsearchedText(e);
                    }}
                />

                <div className="d-flex align-items-center">
                    <span className="flex-1 fs-12 fw-400 c-bg-400 ellipsis-text">
                        {formatNumber(segmentCount)}
                        {" "}
                        reports are available for this category.
                    </span>
                    <span className="fs-12 fw-400 c-b-400">
                        Reports Included:
                        {" "}
                        {checkedKeys.length ? formatNumber(checkedKeys.length) : 0}
                    </span>
                </div>
                {isLoading ? null : lowActComponent}
                <div className="report--addbehaviouralsegment--conttree">
                    <TreeProgress percentage={percentage} />
                    {!isLoading ? (
                        <Tree
                            checkable
                            expandedKeys={expandedKeys}
                            checkedKeys={checkedKeys.map((checked) => checked.key)}
                            onExpand={onExpand}
                            onCheck={onCheck}
                            treeData={toDisplayData}
                            showIcon
                            titleRender={(item) => {
                                const {
                                    name, children, key, folder, lowActivity
                                } = item;
                                return (
                                    <>
                                        {(children || folder) && (
                                            <>
                                                {expandedKeys.includes(key)
                                                    ? <FolderOpenFilled style={folderStyle} />
                                                    : <FolderFilled style={folderStyle} />}
                                            </>
                                        )}
                                        {lowActivity && (
                                            <Tooltip title="Low Activity" placement="right">
                                                <WarningOutlined
                                                    style={{
                                                        color: "#faad14",
                                                        fontSize: "3mm",
                                                        marginRight: "2mm",
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                        {getName(name, searchedList)}
                                    </>
                                );
                            }}
                        />
                    ) : <Skeleton paragraph={{ rows: 5 }} active shape="round" />}
                    {emptyComponent()}
                </div>
            </div>
        </Modal>
    );
}

export default AddBehavioralSegmentModal;
